<template>
  <div class="filtro-modal-container">
    <!-- Filter Modal -->
    <Modal
      :elemId="modalId"
      :title="t('Filtros')"
      :show="showFilterModal"
      :closeLabel="t('Cancelar')"
      :saveLabel="t('Aplicar')"
      :saveFn="handleApplyFilter"
      :saveDisabledFn="() => loading"
      modalSize="modal-lg"
      @close="closeFilterModal"
      @dismiss="closeFilterModal"
    >
      <div class="modal-body-content">
        <!-- Franchises Section -->
        <box :title="`${t('Franquias')}`" type="primary">
          <div class="box-content">
              <!-- Alert for date range -->
              <Message v-if="showDateAlert" severity="warn" :closable="false">
                <template #icon>
                  <i class="pi pi-exclamation-triangle"></i>
                </template>
                <div class="date-alert-content">
                  <strong>{{ t('ATENÇÃO')}}:</strong>
                  {{ t('O período selecionado no filtro não contempla a data de hoje. Alguns dados como orçamentos e compromissos recém adicionados podem não aparecer.') }}
                </div>
              </Message>

              <!-- Select All Franchises -->
              <div class="select-all-container">
                <Button
                  data-testid="filter-select-all-franchises-btn"
                  @click="selectAllFranchises"
                  :label="t('Todas')"
                  :icon="allFranchisesSelected ? 'pi pi-check' : ''"
                  severity="secondary"
                  outlined
                  class="select-all-button"
                />
              </div>

              <!-- Selected Franchises Tags -->
              <div class="selected-franchises-container">
                <Button
                  v-for="franchise in selectedFranchises"
                  :key="franchise.id"
                  :data-testid="`filter-franchise-${franchise.id}-tag`"
                  :label="franchise.name"
                  icon="pi pi-times"
                  iconPos="right"
                  size="small"
                  severity="secondary"
                  outlined
                  @click="toggleFranchise(franchise)"
                  class="franchise-tag"
                />
              </div>

              <!-- Search Input -->
              <div class="search-container">
                <input-text
                  data-testid="filter-search-input"
                  elem-id="filtro_search"
                  :modelValue="searchTerm"
                  @update:modelValue="handleSearchInput"
                  :placeholder="t('Buscar')"
                  icon="pi pi-search"
                  class="search-input"
                />
              </div>

              <!-- Franchises List -->
              <div class="franchises-list" role="table">
                <div class="franchises-list-header" role="row">
                  <div class="franchises-col-status" role="columnheader">{{ t('Status') }}</div>
                  <div class="franchises-col-name" role="columnheader">{{ t('Franquia') }}</div>
                  <div class="franchises-col-employees" role="columnheader">{{ t('Colaboradores') }}</div>
                </div>
                <div class="franchises-list-body">
                  <div
                    v-for="franchise in filteredFranchises"
                    :key="franchise.id"
                    class="franchises-list-row"
                    role="row"
                  >
                    <div class="franchises-col-status" role="cell">
                      <Toggle
                        :elemId="`franchise-toggle-${franchise.id}`"
                        :data-testid="`filter-franchise-toggle-${franchise.id}`"
                        :modelValue="isFranchiseSelected(franchise.id)"
                        @update:modelValue="toggleFranchise(franchise)"
                      />
                    </div>
                    <div class="franchises-col-name" role="cell">{{ franchise.name }}</div>
                    <div class="franchises-col-employees" role="cell">
                      <Button
                        :data-testid="`filter-franchise-employees-${franchise.id}-btn`"
                        @click="openEmployeesModal(franchise)"
                        :label="t('Colaboradores')"
                        severity="primary"
                        size="small"
                        :disabled="!isFranchiseSelected(franchise.id)"
                      />
                    </div>
                  </div>
                </div>
              </div>
          </div>
        </box>

        <!-- Date Range Section -->
        <box :title="`${t('Período')}`" type="primary">
          <div class="box-content">
              <div class="date-picker-container" data-testid="filter-period-select">
                <date-range
                  elem-id="filter-period"
                  :modelValue="dateRangeValue"
                  @update:modelValue="handleDateRangeUpdate"
                  :placeholder="t('Selecione o período')"
                  :showPredefinedButtons="true"
                  :predefinedButtons="dateRangePredefinedButtons"
                />
              </div>
          </div>
        </box>
      </div>
    </Modal>

    <!-- Employees Modal -->
    <Modal
      :elemId="`modalFuncionariosFranquia_${componentId}`"
      :title="currentFranchiseName"
      :show="showEmployeesModal"
      :closeLabel="t('Fechar')"
      @close="closeEmployeesModal"
      @dismiss="closeEmployeesModal"
    >
      <div class="employees-modal-content">
        <div class="employees-list" role="table">
          <div class="employees-list-header" role="row">
            <div class="employee-name-col" role="columnheader">{{ t('Nome') }}</div>
            <div class="employee-select-col" role="columnheader">{{ t('Selecionado') }}</div>
          </div>
          <div class="employees-list-body">
            <div
              v-for="employee in currentFranchiseEmployees"
              :key="employee.funcionario_id"
              class="employees-list-row"
              role="row"
            >
              <div class="employee-name-cell" role="cell">{{ employee.funcionario_nome }}</div>
              <div class="employee-select-cell" role="cell">
                <Toggle
                  :elemId="`employee-toggle-${employee.funcionario_id}`"
                  :data-testid="`filter-employee-toggle-${employee.funcionario_id}`"
                  :modelValue="isEmployeeSelected(employee.funcionario_id)"
                  @update:modelValue="toggleEmployee(employee)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFiltro } from '../../composables/useFiltro';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import Modal from '../../ui/Modal.vue';

type Id = number | string;

interface Franchise {
  id: Id;
  name: string;
}

interface Employee {
  funcionario_id: Id;
  funcionario_nome: string;
}

interface PredefinedButton {
  period: string;
  label: string;
}

interface Props {
  componentId?: string;
  dateRangePickerConfig?: Record<string, unknown>;
}

const props = withDefaults(defineProps<Props>(), {
  componentId: () => `filtro_${Date.now()}`,
  dateRangePickerConfig: () => ({})
});

const emit = defineEmits<{
  'filter-applied': [payload: Record<string, unknown>];
  'filter-error': [error: unknown];
}>();

const { t, locale } = useI18n();

import type { Ref } from 'vue';

const filtro = useFiltro({ dateRangePickerConfig: props.dateRangePickerConfig });
const filterData = filtro.filterData;
const selectedFranchises = filtro.selectedFranchises as unknown as Ref<Franchise[]>;
const showDateAlert = filtro.showDateAlert;
const loading = filtro.loading;
const allFranchisesSelected = filtro.allFranchisesSelected;
const searchTerm = filtro.searchTerm;
const currentFranchiseId = filtro.currentFranchiseId;
const currentFranchiseName = filtro.currentFranchiseName;
const currentFranchiseEmployees = filtro.currentFranchiseEmployees as unknown as Ref<Employee[]>;
const dateRangeValue = filtro.dateRangeValue;
const filteredFranchises = filtro.filteredFranchises as unknown as Ref<Franchise[]>;
const selectAllFranchises = filtro.selectAllFranchises;
const toggleFranchise = filtro.toggleFranchise as unknown as (franchise: Franchise) => void;
const isFranchiseSelected = filtro.isFranchiseSelected as unknown as (id: Id) => boolean;
const setCurrentFranchise = filtro.setCurrentFranchise as unknown as (franchise: Franchise) => void;
const toggleEmployee = filtro.toggleEmployee as unknown as (employee: Employee) => void;
const isEmployeeSelected = filtro.isEmployeeSelected as unknown as (id: Id) => boolean;
const updateDateRange = filtro.updateDateRange;
const applyFilter = filtro.applyFilter;
const setSearchTerm = filtro.setSearchTerm;

const modalId = ref<string>(`modalFiltro_${props.componentId}`);
const showFilterModal = ref<boolean>(false);
const showEmployeesModal = ref<boolean>(false);
const dateRangePredefinedButtons = ref<PredefinedButton[]>([
  { period: 'today', label: t('Hoje') },
  { period: 'yesterday', label: t('Ontem') },
  { period: '7days', label: t('Últimos 7 dias') },
  { period: '30days', label: t('Últimos 30 dias') },
  { period: 'thismonth', label: t('Este mês') },
  { period: 'lastmonth', label: t('Mês passado') }
]);

// DateRange now speaks ISO `YYYY-MM-DD - YYYY-MM-DD` on its v-model contract,
// which is exactly the format useFiltro stores internally, so this is a
// pass-through with a shape guard.
const handleDateRangeUpdate = (dateRangeStr: string): void => {
  log('FiltroModal', 'handleDateRangeUpdate', 'DateRange updated', { dateRangeStr });
  if (typeof dateRangeStr !== 'string' || !dateRangeStr.includes(' - ')) return;
  updateDateRange(dateRangeStr);
};

const handleSearchInput = async (value: string | number | null | undefined): Promise<void> => {
  await setSearchTerm(String(value ?? ''));
};

const openEmployeesModal = (franchise: Franchise): void => {
  setCurrentFranchise(franchise);
  showEmployeesModal.value = true;
};

const closeEmployeesModal = (): void => {
  showEmployeesModal.value = false;
};

const handleApplyFilter = async (): Promise<void> => {
  try {
    log('FiltroModal', 'handleApplyFilter', 'Applying filter', { modalId: modalId.value });
    await applyFilter();
    closeFilterModal();
    eventBus.emit('filtro.modal.closed', { modalId: modalId.value });
    emit('filter-applied', {
      filterData: filterData.value,
      selectedFranchises: selectedFranchises.value
    });
  } catch (error) {
    log('FiltroModal', 'handleApplyFilter', 'Error applying filter', { error });
    console.error('Error applying filter:', error);
    emit('filter-error', error);
    alert(t('Não foi possível aplicar o filtro'));
  }
};

const closeFilterModal = (): void => {
  showFilterModal.value = false;
};

const openModal = (): void => {
  log('FiltroModal', 'openModal', 'Opening modal', { modalId: modalId.value });
  showFilterModal.value = true;
  eventBus.emit('filtro.modal.opened', { modalId: modalId.value });
};

defineExpose({
  openModal
});

watch(filteredFranchises, () => {
  // Reactive recompute hook (no-op).
}, { immediate: true });

onMounted(() => {
  log('FiltroModal', 'onMounted', 'Component mounted');
});
</script>

<style scoped>
.filtro-modal-container {
  position: relative;
}

.modal-body-content {
  background-color: #e2e2e2;
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

/* Box Content */
.box-content {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

/* Date Alert Message */
.date-alert-content {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

/* Select All Button */
.select-all-container {
  margin-bottom: 15px;
}

.select-all-button {
  width: 100%;
}

/* Selected Franchises Tags */
.selected-franchises-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 15px;
  min-height: 36px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 6px;
  position: static;
  z-index: auto;
}

.franchise-tag {
  flex-shrink: 0;
}

/* Search Container */
.search-container {
  margin-bottom: 20px;
}

.search-input {
  width: 100%;
}

/* Franchises list (replaces primevue DataTable) */
.franchises-list {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  max-height: 400px;
  overflow-y: auto;
  background: #fff;
}

.franchises-list-header,
.franchises-list-row {
  display: grid;
  grid-template-columns: 80px 1fr 130px;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
}

.franchises-list-header {
  font-weight: 600;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  position: sticky;
  top: 0;
  z-index: 1;
}

.franchises-list-row {
  border-bottom: 1px solid #f0f0f0;
}

.franchises-list-row:last-child {
  border-bottom: none;
}

.franchises-col-status {
  text-align: center;
}

.franchises-col-employees {
  text-align: right;
}

/* Date Picker Container */
.date-picker-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
}


/* Employees Modal */
.employees-modal-content {
  padding: 15px;
}

.employees-list {
  width: 100%;
  background: white;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #dee2e6;
}

.employees-list-header,
.employees-list-row {
  display: grid;
  grid-template-columns: 1fr 150px;
  gap: 8px;
  align-items: center;
  padding: 10px 12px;
}

.employees-list-header {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #dee2e6;
}

.employees-list-row {
  border-bottom: 1px solid #dee2e6;
}

.employees-list-row:last-child {
  border-bottom: none;
}

.employees-list-row:hover {
  background-color: #f8f9fa;
}

.employee-name-cell {
  font-weight: 500;
  color: #333;
}

.employee-select-cell {
  text-align: right;
  white-space: nowrap;
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-body-content {
    padding: 10px;
  }

  .selected-franchises-container {
    padding: 6px;
  }

  .franchises-list-header,
  .franchises-list-row {
    grid-template-columns: 60px 1fr 100px;
    padding: 8px 6px;
    font-size: 0.85rem;
  }

  .employees-list-header,
  .employees-list-row {
    padding: 8px;
    font-size: 0.9rem;
  }

  .employee-select-cell {
    text-align: center;
  }
}

@media (max-width: 480px) {
  .franchises-list-header,
  .franchises-list-row {
    grid-template-columns: 50px 1fr 85px;
    padding: 6px 4px;
    font-size: 0.75rem;
  }

  .employees-list-header,
  .employees-list-row {
    padding: 6px;
    font-size: 0.8rem;
  }
}

/* Animation classes */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
